import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
} from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../../Store/Store';
import { useNavigationTracking } from '../../hooks/useNavigationTracking';

import FeedbackScreen from './Screens/FeedBackScreen';
import FeedbackScreenHold from './Screens/FeedBackScreenHold';
import StarsScreen from './Screens/StarsScreen';
import AnimationScreen from './Screens/AnimationScreen';
import DigitalContentPdfScreen from './Screens/DigitalContentPdfScreen';
import CustomerIdScreen from './Screens/CustomerIdScreen';
import RegistrationScreen from './Screens/RegistrationScreen';
import NoExecutionsScreen from './Screens/NoExecutionsScreen';
import GeoRegisterScreen from './Screens/Geo/GeoRegisterScreen';
import GeoPositionScreen from './Screens/Geo/GeoPositionScreen';
import GeoVerifyScreen from './Screens/Geo/GeoVerifyScreen';
import GeoProcessingScreen from './Screens/Geo/GeoProcessingScreen';
import CodeScannerScreen from './Screens/CodeScannerScreen';
import CodeScannerPistolScreen from './Screens/CodeScannerPistolScreen';
import RegistrationScannerScreen from './Screens/RegistrationScannerScreen';
import PhotoFeedbackScreen from './Screens/PhotoFeedbackScreen';
import ConsentScreen from './Screens/ConsentScreen';
import DeviceDataScreen from './Screens/DeviceDataScreen';
import ProductStatusScreen from './ProductStatusScreen';
import LinksScreen from './Screens/LinksScreen';
import { buildScreenSequence, feedbackScreenIndex } from './screenSequence';

// Screen che raccolgono dati obbligatori: il pulsante di skip al prossimo screen
// non va mai mostrato, qualunque cosa dicano EventScreenOrder o
// Product.show_skip_next_screen_button. Saltare consent manda al feedback un
// payload senza consensi, che il gate server-side rifiuta con 422.
const NO_SKIP_SCREENS = ['consent', 'device-data', 'device-data-consumer'];

export default function ScreenRouter() {
  const {
    token,
    product,
    event,
    appState,
    backendBaseUrl,
    userEmail,
    setUserEmail,
    fetchData,
    style,
    isEventMode,
    eventProducts,
    cycleSessionId,
    startNewCycle,
    getCycleResumeIndex,
    setCycleResumeIndex,
    shouldResumeAtFeedback,
    markResumeAtFeedback,
    setCurrentScreenKey,
    getLinksReturnTo,
    clearLinksReturnTo,
  } = useAppContext();

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { trackNavigation } = useNavigationTracking();

  const [screens, setScreens] = useState<string[]>([]);
  const [rawScreens, setRawScreens] = useState<string[]>([]);
  const [screenConfigs, setScreenConfigs] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [shouldSkipRegistration, setShouldSkipRegistration] = useState(false);
  const [screenRepeatCounts, setScreenRepeatCounts] = useState<
    Record<string, number>
  >({});
  const routeMap: Record<string, string> = {
    registration: '/registration',
    feedback: '/feedback',
    'feedback-hold': '/feedback-hold',
    stars: '/stars',
    animation: '/animation',
    'digital-content-pdf': '/digital-content-pdf',
    customer_id: '/customer-id',
    'geo-register-screen': '/geo-register-screen',
    'geo-position-screen': '/geo-position-screen',
    'geo-verify-screen': '/geo-verify-screen',
    'geo-processing': '/geo-processing',
    codescanner: '/codescanner',
    codescannerpistol: '/codescannerpistol',
    registrationscanner: '/registrationscanner',
    'photo-feedback': '/photo-feedback',
    consent: '/consent',
    'device-data': '/device-data',
    'device-data-consumer': '/device-data-consumer',
    links: '/links',
  };

  // Internal state for current screen instead of URL-based
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);

  // Track when we're doing a programmatic redirect (not browser back)
  const isRedirectingRef = useRef(false);
  const autoSkipCustomerIdRef = useRef(false);
  // Initial screen index requested via ?start_index= (e.g. a codescanner redirect
  // that must resume AFTER the scanner screen). Honored once, when the product loads,
  // so the product-change reset below doesn't bounce us back to screen 0 (the scanner).
  const pendingStartIndexRef = useRef<number | null>(null);
  // Codice del prodotto per cui l'indice di partenza e' gia' stato deciso.
  // Serve perche' ripulire l'URL con replaceState non aggiorna la location di
  // react-router: start_index puo' ricomparire nei searchParams e riapplicarlo
  // riporterebbe l'utente indietro.
  const resumeResolvedRef = useRef<string | null>(null);
  // Prodotto per cui la ripresa "al feedback" (senza indice) e' gia' stata valutata.
  const feedbackResumeAppliedRef = useRef<string | null>(null);
  // Products visited in the current cycle — used to detect when a redirect_product
  // is going back to an already-visited product (cycle restart) vs an intermediate hop
  const visitedProductsInCycleRef = useRef<Set<string>>(new Set());
  // History of {productCode, screenIndex} we moved away FROM on every forward step,
  // so the Back button can return to the exact previous product/screen.
  const navigationHistoryRef = useRef<
    Array<{ productCode: string; screenIndex: number }>
  >([]);

  const noRemainingExecutions =
    product?.id != -1 && product?.remaining_executions <= 0;

  useLayoutEffect(() => {
    const code = product?.code;
    if (!code) return;
    // Decisione una volta sola per prodotto: senza questo, ogni cambio di
    // searchParams (cioe' ogni navigazione tra gli screen dello stesso prodotto)
    // riapplicherebbe l'indice di ripresa e riporterebbe l'utente indietro.
    if (resumeResolvedRef.current === code) return;

    // Aspetta che il prodotto a cui l'indice si riferisce sia davvero caricato.
    // Tra il navigate('/?product=NEW&start_index=N') e il load del prodotto,
    // ProductPage rende una prima volta col prodotto VECCHIO (appState ancora
    // 'initial'): ScreenRouter monta, poi fetchData porta appState a 'loading' e
    // lo smonta. Consumare l'indice in quel giro transitorio lo perdeva insieme
    // ai ref, e al rimontaggio si ripartiva da 0 — cioe' gli screen prima del
    // feedback (consent) rimostrati a ogni prodotto per order.
    const targetProduct = searchParams.get('product');
    if (targetProduct && targetProduct !== code) return;

    resumeResolvedRef.current = code;

    const idxParam = searchParams.get('start_index');
    // Fallback sull'indice registrato nel ciclo: l'URL viene ripulito subito dopo
    // l'applicazione, quindi un qualunque rimontaggio successivo (o un reload)
    // resta senza start_index e ripartirebbe da 0, rimostrando consent/device data
    // per ogni prodotto. La copia in sessionStorage sopravvive.
    const storedIdx = getCycleResumeIndex(code);
    const idx =
      idxParam !== null ? parseInt(idxParam, 10) || 0 : (storedIdx ?? 0);

    if (idxParam !== null) {
      console.log(
        `📍 start_index=${idxParam} detected in URL, setting initial index to ${idx}`
      );
      setCycleResumeIndex(code, idx);
    } else if (idx > 0) {
      console.log(`📍 Resuming product ${code} at screen index ${idx} (cycle)`);
    }

    if (idx > 0) {
      pendingStartIndexRef.current = idx;
      setCurrentScreenIndex(idx);
    }

    if (idxParam !== null) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('start_index');
      const newSearch = newSearchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      console.log(`🗑️ Removing start_index from URL immediately: ${newUrl}`);
      window.history.replaceState({}, '', newUrl);
    }
    // Depend on searchParams (not just mount) so a SPA navigate() carrying
    // start_index — e.g. the Back button crossing a product boundary — is
    // honored too, not only full-page redirects (codescanner flow). product?.code
    // is a dep because the index is applied only once that product is loaded.
  }, [searchParams, product?.code, getCycleResumeIndex, setCycleResumeIndex]);

  useEffect(() => {
    const productParam = searchParams.get('product');
    const tokenParam = searchParams.get('token');

    const terminalStates = ['not-found', 'error', 'no-token', 'managetag'];
    if (terminalStates.includes(appState)) return;

    if ((productParam || tokenParam) && !product && appState !== 'loading') {
      fetchData();
    }

    if (
      productParam &&
      product &&
      productParam !== product.code &&
      appState !== 'loading'
    ) {
      fetchData();
    }
  }, [searchParams, product, appState, fetchData]);

  useEffect(() => {
    // On product load (each product is a fresh URL load), start at the first screen —
    // unless a ?start_index= was requested (codescanner / Back resume).
    // Wait until the product is actually loaded before deciding the screen index,
    // otherwise we'd reset to 0 at mount and clobber a pending start_index.
    if (!product?.code) return;
    if (pendingStartIndexRef.current !== null) {
      // A ?start_index= was requested (codescanner redirect): resume there, once.
      setCurrentScreenIndex(pendingStartIndexRef.current);
      pendingStartIndexRef.current = null;
      return;
    }
    setCurrentScreenIndex(0);
  }, [product?.code]);

  useEffect(() => {
    setFeedbackData(null);

    if (isRedirectingRef.current) {
      isRedirectingRef.current = false;
    }
  }, [product?.code]);

  useEffect(() => {
    if (product?.code) {
      visitedProductsInCycleRef.current.add(product.code);
    }
  }, [product?.code]);

  const getCurrentScreenIndex = useCallback(() => {
    return currentScreenIndex;
  }, [currentScreenIndex]);

  const getCurrentScreen = useCallback(() => {
    return screens[currentScreenIndex] || 'feedback';
  }, [screens, currentScreenIndex]);

  // Pubblica lo screen corrente nello store. L'Header vive fuori dal body (sta in
  // App, accanto a questo router) e senza questo non saprebbe distinguere uno
  // screen di risposta dagli altri, mostrando la domanda del prodotto su tutti.
  // Finche' la sequenza non e' risolta il valore resta null: meglio nessuna
  // domanda che la domanda sullo screen sbagliato.
  useEffect(() => {
    setCurrentScreenKey(
      screens.length > 0 ? (screens[currentScreenIndex] ?? null) : null
    );
    return () => setCurrentScreenKey(null);
  }, [screens, currentScreenIndex, setCurrentScreenKey]);

  useEffect(() => {
    if (appState !== 'initial') return;

    if (!product || !event) {
      // If we don't have product/event data yet, don't show loading for screens
      // The main loading condition will handle showing loading while data is being fetched
      setLoading(false);
      return;
    }

    const fetchScreens = async () => {
      try {
        const response = await axios.get(`${backendBaseUrl}/event-screens/`, {
          params: { event_id: event.id },
        });
        const fetched = response.data.screens as string[];
        const configs =
          (response.data.screen_configs as Record<string, any>) || {};
        // Modo 1 (type sul prodotto) fuso con Modo 2/3 (sequenza evento/event-type):
        // la schermata del type va in testa, senza duplicare se gia' presente.
        // In ingresso per evento (multiquestion) il Modo 1 non si applica: comanda
        // l'ordine dell'evento/event-type. Vedi buildScreenSequence.
        const finalScreens = buildScreenSequence(fetched, product, {
          isEventEntry: isEventMode,
        });
        setScreens(finalScreens);
        // Sequenza grezza dell'evento/event-type, prima del merge col type del
        // prodotto: serve a calcolare la sequenza del PROSSIMO prodotto per order
        // (e quindi dove sta il suo screen di feedback) senza rifare la fetch.
        setRawScreens(fetched);
        setScreenConfigs(configs);
        setScreenRepeatCounts({});
      } catch (error) {
        console.error('Failed to fetch screens:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchScreens();
  }, [
    event,
    token,
    product,
    appState,
    backendBaseUrl,
    searchParams,
    isEventMode,
  ]);

  // Ripresa senza indice noto: il prodotto e' stato marcato come "hop dello stesso
  // ciclo" (vedi FeedbackCycle.resumeAtFeedback) ma il suo indice di feedback non
  // era calcolabile alla partenza — succede nella catena redirect_product quando il
  // prodotto di destinazione non e' fra quelli per order dell'evento, quindi il suo
  // type non era noto. Qui la sequenza vera c'e', quindi l'indice si risolve su
  // quella e gli screen di raccolta dati (consent, device data, photo) non vengono
  // rimostrati. Una volta sola per prodotto: dopo, l'utente naviga normalmente.
  useEffect(() => {
    const code = product?.code;
    if (!code || screens.length === 0) return;
    // Un start_index esplicito vince sempre: e' il Back che torna esattamente allo
    // screen da cui l'utente e' arrivato, non un hop in avanti da riposizionare.
    if (searchParams.get('start_index') !== null) return;
    if (feedbackResumeAppliedRef.current === code) return;
    feedbackResumeAppliedRef.current = code;
    if (!shouldResumeAtFeedback(code)) return;
    const idx = feedbackScreenIndex(screens, product);
    if (idx > 0) {
      console.log(
        `📍 Resuming product ${code} at its feedback screen (${idx})`
      );
      setCurrentScreenIndex(idx);
    }
  }, [product, screens, searchParams, shouldResumeAtFeedback]);

  useEffect(() => {
    if (!product || screens.length === 0) return;
    const currentKey = getCurrentScreen();
    const desiredPath = routeMap[currentKey] || '/';
    const currentPath = location.pathname;
    if (desiredPath !== currentPath) {
      const params = new URLSearchParams(location.search);
      // start_index gia' applicato: qui va tolto, altrimenti rientra nell'URL.
      // replaceState non aggiorna la location di react-router, quindi
      // location.search lo contiene ancora anche dopo la pulizia. Solo dopo
      // l'applicazione: nel giro transitorio deve restare per il rimontaggio.
      if (resumeResolvedRef.current !== null) {
        params.delete('start_index');
      }
      const search = params.toString();
      const qs = search ? `?${search}` : '';
      navigate(`${desiredPath}${qs}`);
    }
  }, [
    screens,
    currentScreenIndex,
    location.search,
    getCurrentScreen,
    navigate,
  ]);

  const navigateToProduct = useCallback(
    (
      productCode: string,
      preserveCustomerId: boolean = false,
      startIndex?: number
    ) => {
      isRedirectingRef.current = true;
      // Hop dentro lo STESSO ciclo (prodotti per order o catena
      // redirect_with_same_customer_id): non si tocca nulla, customer_id,
      // session_id, consensi e device data del ciclo sono persistiti e vengono
      // riusati dal prossimo prodotto. Altrimenti il ciclo ricomincia da zero.
      if (!preserveCustomerId) {
        startNewCycle();
      } else if (typeof startIndex === 'number') {
        // Registra subito l'indice di ripresa: il prossimo prodotto lo trova
        // anche se start_index si perde per strada (URL ripulito, rimontaggio).
        setCycleResumeIndex(productCode, startIndex);
      } else {
        // Stesso ciclo ma indice non calcolabile (prodotto fuori dall'order
        // dell'evento): si marca soltanto "riparti dal feedback" e l'indice viene
        // risolto quando la sequenza del prodotto e' nota. Senza questo il
        // prodotto ripartirebbe da 0 e rimostrerebbe consent / device data / photo.
        markResumeAtFeedback(productCode);
      }
      const startIndexQs =
        typeof startIndex === 'number' ? `&start_index=${startIndex}` : '';
      navigate(`/?product=${productCode}${startIndexQs}`);
    },
    [navigate, startNewCycle, setCycleResumeIndex, markResumeAtFeedback]
  );

  // Tasto Indietro del browser dopo aver seguito un link dallo screen Links: la
  // history nativa non e' affidabile qui, perche' ogni prodotto caricato fa un
  // navigate(..., {replace:true}) verso il proprio primo screen (RoutedBody),
  // consumando l'entry che il back dovrebbe raggiungere. Se l'utente e' arrivato
  // al prodotto corrente da uno screen Links (vedi LinksScreen.setLinksReturnTo),
  // il back riparte esplicitamente da quel prodotto invece di affidarsi a
  // window.history, che salterebbe oltre lo screen Links.
  useEffect(() => {
    const handlePopState = () => {
      const returnTo = getLinksReturnTo();
      if (!returnTo) return;
      clearLinksReturnTo();
      navigateToProduct(returnTo, false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getLinksReturnTo, clearLinksReturnTo, navigateToProduct]);

  // autoSkipArg is true when the advance is programmatic (geo/customer_id/registration
  // auto-skips, geo-verify chains). Such steps are invisible to the user, so they
  // must NOT be pushed onto navigationHistoryRef — otherwise the Back button would
  // land on a screen the user never saw and get bounced forward again.
  // NB: several screens wire onClick={onNext} directly, so this can also receive a
  // React event as first arg — only a strict boolean true counts as an auto-skip.
  const nextScreen = useCallback(
    (autoSkipArg?: unknown) => {
      const isAutoSkip = autoSkipArg === true;
      const currentIndex = getCurrentScreenIndex();
      const currentScreen = screens[currentIndex];

      const repeatCount = screenConfigs[currentScreen]?.repeat_count || 1;
      const currentRepeatCount = screenRepeatCounts[currentScreen] || 0;

      console.log(`🔄 nextScreen called for "${currentScreen}":`, {
        currentRepeatCount,
        repeatCount,
        willRepeat: currentRepeatCount < repeatCount - 1,
      });

      if (currentRepeatCount < repeatCount - 1) {
        console.log(
          `🔁 Incrementing repeat count for "${currentScreen}" from ${currentRepeatCount} to ${currentRepeatCount + 1} (not navigating yet)`
        );
        setScreenRepeatCounts(prev => ({
          ...prev,
          [currentScreen]: currentRepeatCount + 1,
        }));

        if (currentScreen) {
          trackNavigation({
            from_screen: currentScreen,
            to_screen: currentScreen,
            navigation_type: 'repeat',
            screen_index: currentIndex,
            total_screens: screens.length,
          });
        }

        return;
      }

      console.log(
        `✅ Repeat count completed for "${currentScreen}", proceeding to next screen`
      );

      // Record where we're moving away FROM so the Back button can return here,
      // even if the step below crosses over to a different product. Skip programmatic
      // auto-skips: the user never saw that screen, so it must not be a Back target.
      if (product?.code && !isAutoSkip) {
        navigationHistoryRef.current.push({
          productCode: product.code,
          screenIndex: currentIndex,
        });
      }

      setScreenRepeatCounts(prev => {
        const newCounts = { ...prev };
        delete newCounts[currentScreen];
        return newCounts;
      });

      const isFeedbackScreen =
        currentScreen === 'feedback' ||
        (currentScreen === 'photo-feedback' && product?.type === 'images');
      // How many screens come AFTER the feedback step in the sequence (e.g. animation).
      const hasScreensAfterFeedback =
        isFeedbackScreen && currentIndex < screens.length - 1;

      // Product.order navigation BY URL: when the current product belongs to an event
      // with more than one ordered product (eventProducts, provided by the backend),
      // the feedback screen advances to the NEXT product by order — but navigating the
      // URL to /?product=<nextCode> instead of iterating in place. Each product is a
      // clean load and the URL always reflects the real product (no fixed-URL confusion,
      // no in-memory iteration state to get out of sync).
      //   - Intermediate product (not the last by order): navigate to the next product,
      //     skipping animation & anything after feedback (shown only at the end).
      //   - Last product by order: fall through so the post-feedback screens (animation)
      //     show once; the newIndex === 0 branch below then loops back to the FIRST
      //     product by order (restart), again by URL.
      // orderedProducts is sorted ascending by the backend; we locate the current
      // product by code (the entry product may be anywhere in the order).
      const orderedProducts = eventProducts;
      const orderIndex = orderedProducts.findIndex(
        p => p.code === product?.code
      );
      const isOrderedEvent = orderedProducts.length > 1 && orderIndex !== -1;
      const isLastByOrder = orderIndex === orderedProducts.length - 1;

      // Indice di ripresa per un prodotto dello STESSO ciclo (hop per order o
      // catena redirect_with_same_customer_id). Gli screen PRIMA del feedback
      // (consent, device-data, registration, lo screen forzato dal type, ...) si
      // vedono una volta sola per ciclo: i prodotti successivi ripartono dal loro
      // screen di feedback, non da capo. L'indice va calcolato sulla sequenza del
      // prodotto di destinazione, perche' il merge col suo type puo' spostarla di
      // una posizione rispetto a quella corrente — per questo serve l'oggetto
      // prodotto e non solo il codice. Se il prodotto non e' tra quelli dell'evento
      // (catena con order = -1) il type non e' noto: meglio nessun indice, e la
      // sequenza rimostrata da capo, che un indice sbagliato che salta il feedback.
      const resumeIndexFor = (code: string): number | undefined => {
        const target = orderedProducts.find(p => p.code === code);
        if (!target) return undefined;
        const index = feedbackScreenIndex(
          buildScreenSequence(rawScreens, target),
          target
        );
        return index > 0 ? index : undefined;
      };

      if (isFeedbackScreen && isOrderedEvent && !isLastByOrder) {
        const nextProduct = orderedProducts[orderIndex + 1];
        trackNavigation({
          from_screen: currentScreen,
          to_screen: nextProduct?.code || 'next-order-product',
          navigation_type: 'forward',
          screen_index: currentIndex,
          total_screens: screens.length,
        });
        // preserveCustomerId = true: same session/customer across the ordered products.
        navigateToProduct(
          nextProduct.code,
          true,
          resumeIndexFor(nextProduct.code)
        );
        return;
      }

      // redirect_product chain (single-product entry not part of an ordered event): the
      // products are linked by redirect_product, and each hop remounts ScreenRouter fresh
      // (navigate('/?product=...') -> Main -> RoutedBody -> /feedback), so any in-memory
      // ref is reset per product. When the sequence has screens AFTER the feedback (e.g.
      // animation), those must show only ONCE, after the LAST product of the chain. The
      // reliable, synchronous signal for "am I an intermediate product?" is the current
      // product's redirect_with_same_customer_id: intermediate products keep the same
      // customer_id (=true); the last product loops back with a fresh customer_id (=false).
      if (
        isFeedbackScreen &&
        hasScreensAfterFeedback &&
        !isOrderedEvent &&
        product?.redirect_product &&
        product?.redirect_with_same_customer_id === true
      ) {
        trackNavigation({
          from_screen: currentScreen,
          to_screen: 'redirect',
          navigation_type: 'forward',
          screen_index: currentIndex,
          total_screens: screens.length,
        });
        // Stesso ciclo: il prodotto di destinazione riparte dal suo feedback, come
        // negli hop per order — consent e device data restano visti una volta sola.
        navigateToProduct(
          product.redirect_product,
          true,
          resumeIndexFor(product.redirect_product)
        );
        return;
      }

      const newOrder = currentIndex + 1;
      const newIndex = screens.length ? newOrder % screens.length : 0;
      const toScreen = screens[newIndex];

      // Fine sequenza (newIndex === 0) per un evento ORDINATO: dopo l'ultimo prodotto
      // per order e l'ultimo screen (es. animation), si RIPARTE dal PRIMO prodotto per
      // order — sempre per URL. Cosi' il ciclo evento ricomincia da capo con l'URL in
      // sync. Precede il ramo redirect_product perche' in un evento ordinato l'order
      // comanda sempre sul redirect_product dei prodotti.
      if (newIndex === 0 && isOrderedEvent) {
        const firstProduct = orderedProducts[0];
        trackNavigation({
          from_screen: currentScreen,
          to_screen: firstProduct?.code || 'first-order-product',
          navigation_type: 'forward',
          screen_index: 0,
          total_screens: screens.length,
        });
        // Nuovo ciclo: nuovo customer_id (preserveCustomerId = false).
        navigateToProduct(firstProduct.code, false);
        return;
      }

      // Fine sequenza (newIndex === 0) fuori da un evento ordinato: salto al
      // redirect_product del prodotto (catena redirect classica). Per un prodotto
      // 'images' puro (sequenza = [photo-feedback]) il wrap a index 0 avviene subito.
      if (newIndex === 0 && product?.redirect_product) {
        trackNavigation({
          from_screen: currentScreen,
          to_screen: 'redirect',
          navigation_type: 'forward',
          screen_index: currentIndex,
          total_screens: screens.length,
        });

        if (visitedProductsInCycleRef.current.has(product.redirect_product)) {
          visitedProductsInCycleRef.current.clear();
        }

        const sameCycle = Boolean(product.redirect_with_same_customer_id);
        navigateToProduct(
          product.redirect_product,
          sameCycle,
          sameCycle ? resumeIndexFor(product.redirect_product) : undefined
        );
        return;
      }

      if (currentScreen && toScreen) {
        trackNavigation({
          from_screen: currentScreen,
          to_screen: toScreen,
          navigation_type: 'forward',
          screen_index: newIndex,
          total_screens: screens.length,
        });
      }

      setCurrentScreenIndex(newIndex);

      if (newIndex === 0) {
        visitedProductsInCycleRef.current.clear();
        // New cycle: previous cycle's steps are no longer valid Back targets.
        navigationHistoryRef.current = [];
        // Fine del giro per un prodotto singolo (i rami per order / redirect_product
        // sono usciti sopra): il ciclo ricomincia, quindi nuovo customer_id e
        // consensi/device data da richiedere di nuovo.
        startNewCycle();
        if (!event?.remember_registration) {
          setUserEmail('');
          sessionStorage.removeItem('userEmail');
        }
      }
    },
    [
      screens,
      getCurrentScreenIndex,
      getCurrentScreen,
      trackNavigation,
      event,
      setUserEmail,
      navigate,
      searchParams,
      product,
      fetchData,
      feedbackData,
      navigateToProduct,
      screenConfigs,
      screenRepeatCounts,
      eventProducts,
      rawScreens,
      startNewCycle,
    ]
  );

  const goBack = useCallback(() => {
    const currentOrder = getCurrentScreenIndex();
    const currentIndex = getCurrentScreenIndex();
    const newOrder = currentOrder > 0 ? currentOrder - 1 : 0;
    const newIndex = screens.indexOf(screens[newOrder] || screens[0]);

    const fromScreen = screens[currentIndex];
    const toScreen = screens[newIndex];

    if (fromScreen && toScreen) {
      trackNavigation({
        from_screen: fromScreen,
        to_screen: toScreen,
        navigation_type: 'back',
        screen_index: newIndex,
        total_screens: screens.length,
      });
    }

    // Clear feedbackData when navigating back to feedback screen
    if (toScreen === 'feedback' || toScreen === 'feedback-hold') {
      setFeedbackData(null);
    }

    // Ensure we keep index within bounds
    setCurrentScreenIndex(newIndex >= 0 ? newIndex : 0);
  }, [
    screens,
    getCurrentScreenIndex,
    getCurrentScreen,
    trackNavigation,
    navigate,
    searchParams,
    setFeedbackData,
  ]);

  // Back button: returns to wherever the user actually came from, popped from
  // navigationHistoryRef. Unlike goBack() (always "one screen back" within the
  // current product, used internally e.g. after deleting feedback), this also
  // crosses back over a product change (multiquestion or redirect_product chain).
  const goToPreviousStep = useCallback(() => {
    const prev = navigationHistoryRef.current.pop();
    if (!prev) return;

    const fromScreen = getCurrentScreen();

    if (prev.productCode !== product?.code) {
      trackNavigation({
        from_screen: fromScreen,
        to_screen: prev.productCode,
        navigation_type: 'back',
        screen_index: prev.screenIndex,
        total_screens: screens.length,
      });

      // Crossing back to a different product: every product change happens by URL
      // (navigate('/?product=...')), so returning to the previous product is also a
      // URL navigation. Resume at the recorded screen via start_index, preserving the
      // customer_id so Back stays within the same session.
      navigateToProduct(prev.productCode, true, prev.screenIndex);
      return;
    }

    const toScreen = screens[prev.screenIndex];
    if (fromScreen && toScreen) {
      trackNavigation({
        from_screen: fromScreen,
        to_screen: toScreen,
        navigation_type: 'back',
        screen_index: prev.screenIndex,
        total_screens: screens.length,
      });
    }

    if (toScreen === 'feedback' || toScreen === 'feedback-hold') {
      setFeedbackData(null);
    }

    setCurrentScreenIndex(prev.screenIndex);
  }, [
    product,
    screens,
    getCurrentScreen,
    trackNavigation,
    navigateToProduct,
    setFeedbackData,
  ]);

  const resetToScreen = useCallback(
    (screenKey: string) => {
      const screenIndex = screens.indexOf(screenKey);
      if (screenIndex !== -1) {
        setCurrentScreenIndex(screenIndex);

        trackNavigation({
          from_screen: screens[currentScreenIndex] || 'unknown',
          to_screen: screenKey,
          navigation_type: 'skip',
          screen_index: screenIndex,
          total_screens: screens.length,
        });
      } else {
        const feedbackIndex = screens.indexOf('feedback');
        if (feedbackIndex !== -1) {
          setCurrentScreenIndex(feedbackIndex);

          trackNavigation({
            from_screen: screens[currentScreenIndex] || 'unknown',
            to_screen: 'feedback',
            navigation_type: 'skip',
            screen_index: feedbackIndex,
            total_screens: screens.length,
          });
        }
      }
    },
    [
      screens,
      setCurrentScreenIndex,
      trackNavigation,
      currentScreenIndex,
      navigate,
      searchParams,
    ]
  );

  useEffect(() => {
    const currentScreenName = getCurrentScreen();
    if (
      currentScreenName === 'registration' &&
      event?.remember_registration &&
      userEmail
    ) {
      setShouldSkipRegistration(true);
    }
  }, [
    currentScreenIndex,
    event?.remember_registration,
    userEmail,
    getCurrentScreen,
  ]);

  useEffect(() => {
    if (shouldSkipRegistration) {
      nextScreen(true);
      setShouldSkipRegistration(false);
    }
  }, [shouldSkipRegistration, nextScreen]);

  useEffect(() => {
    const currentIndex = getCurrentScreenIndex();
    const nextIndex = currentIndex + 1;
    const nextKey = screens[nextIndex];
    const currentScreenName = getCurrentScreen();

    if (
      product &&
      product.geo_attributes?.position &&
      (style?.geoAttributes?.geo_verify_range ?? null) !== null
    ) {
      if (
        currentScreenName === 'geo-register-screen' ||
        currentScreenName === 'geo-position-screen'
      ) {
        nextScreen(true);
      } else if (
        currentScreenName === 'geo-processing' &&
        nextKey === 'geo-verify-screen'
      ) {
        nextScreen(true);
      }
    }
  }, [
    currentScreenIndex,
    screens,
    product,
    nextScreen,
    getCurrentScreenIndex,
    getCurrentScreen,
  ]);

  useLayoutEffect(() => {
    if (!product || screens.length === 0) return;
    const currentKey = getCurrentScreen();
    const isLastScreen = currentScreenIndex === screens.length - 1;
    const shouldAutoSkip =
      currentKey === 'customer_id' &&
      isLastScreen &&
      Boolean(product.redirect_product) &&
      (isEventMode || Boolean(product.redirect_with_same_customer_id));

    if (shouldAutoSkip && !autoSkipCustomerIdRef.current) {
      autoSkipCustomerIdRef.current = true;
      nextScreen(true);
      return;
    }

    if (!shouldAutoSkip) {
      autoSkipCustomerIdRef.current = false;
    }
  }, [product, screens, currentScreenIndex, getCurrentScreen, nextScreen]);

  useEffect(() => {
    if (screens.length > 0 && currentScreenIndex !== -1) {
      // Changed from currentScreen to currentScreenIndex
      const currentIndex = getCurrentScreenIndex();
      trackNavigation({
        to_screen: screens[currentIndex], // Changed from currentScreen to screens[currentIndex]
        navigation_type: 'direct',
        screen_index: currentIndex,
        total_screens: screens.length,
      });
    }
  }, [currentScreenIndex, screens, trackNavigation, getCurrentScreenIndex]);

  const renderCurrentScreen = () => {
    if (noRemainingExecutions) {
      return <NoExecutionsScreen />;
    }

    if (screens.length === 0) {
      return <FeedbackScreen onNext={nextScreen} deferSave={false} />;
    }

    const currentIndex = getCurrentScreenIndex();
    const nextIndex = currentIndex + 1;
    const nextKey = screens[nextIndex];
    const screenToRender = getCurrentScreen();
    const isLastScreen = currentIndex === screens.length - 1;

    const skipFor = (key: string) =>
      !NO_SKIP_SCREENS.includes(key) &&
      (Boolean(screenConfigs[key]?.show_skip_next_screen_button) ||
        Boolean(product?.show_skip_next_screen_button));

    // Unlike skipFor, eligibility is a per-product, per-screen-type selection
    // (Product.back_button_screen_types), not a screen-order/screen-type config.
    // Never shown on the product's first screen — there's nothing to go back to.
    const backFor = (key: string) =>
      Boolean(product?.back_button_screen_types?.includes(key)) &&
      currentIndex > 0;

    switch (screenToRender) {
      case 'registration':
        if (event?.remember_registration && userEmail) {
          return <div style={{ display: 'none' }}></div>;
        }
        return (
          <RegistrationScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('registration')}
            showBackButton={backFor('registration')}
            onBack={goToPreviousStep}
          />
        );

      case 'feedback': {
        const feedbackKey = `feedback-${currentIndex}-${screenRepeatCounts['feedback'] || 0}-${product?.code || 'p'}`;
        console.log('🎨 Rendering FeedbackScreen with key:', {
          key: feedbackKey,
          currentIndex,
          repeatCount: screenRepeatCounts['feedback'] || 0,
          configuredRepeat: screenConfigs['feedback']?.repeat_count,
        });
        return (
          <FeedbackScreen
            key={feedbackKey}
            onNext={nextScreen}
            deferSave={nextKey === 'stars'}
            setFeedbackData={nextKey === 'stars' ? setFeedbackData : undefined}
            navigateToProduct={navigateToProduct}
            autoSkipSeconds={screenConfigs['feedback']?.auto_skip_seconds || 0}
            showSkipNextScreenButton={skipFor('feedback')}
            showBackButton={backFor('feedback')}
            onBack={goToPreviousStep}
            feedbackSessionId={cycleSessionId}
          />
        );
      }

      case 'feedback-hold':
        return (
          <FeedbackScreenHold
            key={`feedback-hold-${currentIndex}-${screenRepeatCounts['feedback-hold'] || 0}`}
            onNext={nextScreen}
            deferSave={nextKey === 'stars'}
            setFeedbackData={nextKey === 'stars' ? setFeedbackData : undefined}
            navigateToProduct={navigateToProduct}
            showSkipNextScreenButton={skipFor('feedback-hold')}
            showBackButton={backFor('feedback-hold')}
            onBack={goToPreviousStep}
            feedbackSessionId={cycleSessionId}
          />
        );

      case 'stars':
        return (
          <StarsScreen
            key={`stars-${currentIndex}-${screenRepeatCounts['stars'] || 0}`}
            onNext={nextScreen}
            feedbackData={feedbackData}
            setFeedbackData={setFeedbackData}
            goBack={goBack}
            showSkipNextScreenButton={skipFor('stars')}
            showBackButton={backFor('stars')}
            onBack={goToPreviousStep}
          />
        );

      case 'animation':
        return (
          <AnimationScreen
            key={`animation-${currentIndex}-${screenRepeatCounts['animation'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('animation')}
            showBackButton={backFor('animation')}
            onBack={goToPreviousStep}
          />
        );

      case 'digital-content-pdf':
        return (
          <DigitalContentPdfScreen
            onNext={nextScreen}
            resetToScreen={resetToScreen}
            showSkipNextScreenButton={skipFor('digital-content-pdf')}
            showBackButton={backFor('digital-content-pdf')}
            onBack={goToPreviousStep}
          />
        );

      case 'customer_id': {
        const isIntermediateProduct =
          isLastScreen &&
          Boolean(product?.redirect_product) &&
          (isEventMode || Boolean(product?.redirect_with_same_customer_id));
        if (isIntermediateProduct) {
          return <div style={{ display: 'none' }} />;
        }
        return (
          <CustomerIdScreen
            key={`customer_id-${currentIndex}-${screenRepeatCounts['customer_id'] || 0}`}
            onNext={nextScreen}
            autoSkipSeconds={
              screenConfigs['customer_id']?.auto_skip_seconds || 0
            }
            showSkipNextScreenButton={skipFor('customer_id')}
            showBackButton={backFor('customer_id')}
            onBack={goToPreviousStep}
            navigateToProduct={navigateToProduct}
            feedbackSessionId={cycleSessionId}
          />
        );
      }

      case 'geo-register-screen':
        return (
          <GeoRegisterScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('geo-register-screen')}
            showBackButton={backFor('geo-register-screen')}
            onBack={goToPreviousStep}
          />
        );

      case 'geo-position-screen':
        return (
          <GeoPositionScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('geo-position-screen')}
            showBackButton={backFor('geo-position-screen')}
            onBack={goToPreviousStep}
          />
        );

      case 'geo-verify-screen':
        return (
          <GeoVerifyScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('geo-verify-screen')}
            showBackButton={backFor('geo-verify-screen')}
            onBack={goToPreviousStep}
          />
        );

      case 'geo-processing':
        return (
          <GeoProcessingScreen
            key={`geo-processing-${currentIndex}-${screenRepeatCounts['geo-processing'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('geo-processing')}
            showBackButton={backFor('geo-processing')}
            onBack={goToPreviousStep}
          />
        );

      case 'codescanner':
        return (
          <CodeScannerScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('codescanner')}
            showBackButton={backFor('codescanner')}
            onBack={goToPreviousStep}
          />
        );

      case 'codescannerpistol':
        return (
          <CodeScannerPistolScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('codescannerpistol')}
            showBackButton={backFor('codescannerpistol')}
            onBack={goToPreviousStep}
          />
        );

      case 'registrationscanner':
        return (
          <RegistrationScannerScreen
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('registrationscanner')}
            showBackButton={backFor('registrationscanner')}
            onBack={goToPreviousStep}
          />
        );

      case 'photo-feedback':
        return (
          <PhotoFeedbackScreen
            key={`photo-feedback-${currentIndex}-${screenRepeatCounts['photo-feedback'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('photo-feedback')}
            showBackButton={backFor('photo-feedback')}
            onBack={goToPreviousStep}
            feedbackSessionId={cycleSessionId}
          />
        );

      case 'consent':
        return (
          <ConsentScreen
            key={`consent-${currentIndex}-${screenRepeatCounts['consent'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('consent')}
            showBackButton={backFor('consent')}
            onBack={goToPreviousStep}
          />
        );

      case 'device-data':
        return (
          <DeviceDataScreen
            key={`device-data-${currentIndex}-${screenRepeatCounts['device-data'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('device-data')}
            showBackButton={backFor('device-data')}
            onBack={goToPreviousStep}
            showPurchaseDate={false}
            showCodentify={true}
          />
        );

      case 'device-data-consumer':
        return (
          <DeviceDataScreen
            key={`device-data-consumer-${currentIndex}-${screenRepeatCounts['device-data-consumer'] || 0}`}
            onNext={nextScreen}
            showSkipNextScreenButton={skipFor('device-data-consumer')}
            showBackButton={backFor('device-data-consumer')}
            onBack={goToPreviousStep}
            showPurchaseDate={true}
            showCodentify={false}
            showAdcMonths={true}
          />
        );

      case 'links':
        return (
          <LinksScreen
            key={`links-${currentIndex}-${screenRepeatCounts['links'] || 0}`}
            onNext={nextScreen}
            navigateToProduct={navigateToProduct}
            showSkipNextScreenButton={skipFor('links')}
            showBackButton={backFor('links')}
            onBack={goToPreviousStep}
          />
        );

      default:
        return <div>Screen "{screenToRender}" not implemented.</div>;
    }
  };

  // Terminal states (e.g. product not found / deactivated -> 404) must win over the
  // loading guard below, otherwise the screen stays stuck on "Loading..." forever.
  if (
    appState === 'not-found' ||
    appState === 'error' ||
    appState === 'no-token'
  ) {
    return <ProductStatusScreen state={appState} />;
  }

  if (
    loading ||
    appState === 'loading' ||
    (!product && (searchParams.get('product') || searchParams.get('token')))
  ) {
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '1rem',
          boxSizing: 'border-box',
          textAlign: 'center',
        }}
      >
        Loading...
      </div>
    );
  }

  const currentKey = getCurrentScreen();
  const isOverrideFixedHeight =
    currentKey === 'registration' || currentKey === 'digital-content-pdf';
  const isPhotoFeedback = currentKey === 'photo-feedback';
  const containerClass = isOverrideFixedHeight
    ? 'registration-main-product'
    : 'main-product';

  return (
    <div
      className={containerClass}
      style={
        isPhotoFeedback
          ? { width: '100%', boxSizing: 'border-box', overflowY: 'visible' }
          : { width: '100%', flex: 1, minHeight: 0, boxSizing: 'border-box' }
      }
    >
      {renderCurrentScreen()}
    </div>
  );
}
